import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X } from "lucide-react";
import { NAV_LINKS, SITE } from "@/data/site";
import { assetUrl } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Header() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const { count, wishlist, setDrawerOpen, menuOpen: mobileOpen, setMenuOpen: setMobileOpen } = useCart();
  const { user } = useAuth();
  const { settings, categories } = useSite();
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setMobileOpen(false);
      setQ("");
    }
  };

  return (
    <>
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-ivory/85 border-b border-champagne/20 safe-top">
      {/* Announcement bar */}
      <div className="bg-espresso text-ivory text-center text-[11px] tracking-[0.2em] uppercase py-2 font-sans" data-testid="announcement-bar">
        {settings.announcement}
      </div>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-3 items-center h-16 lg:h-[72px]">
          {/* Left: menu button opens category side slider */}
          <div className="flex items-center">
            <button className="flex items-center gap-2 p-1 group" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open" aria-label="Menu">
              <Menu className="w-6 h-6 text-espresso" />
              <span className="hidden lg:inline text-[12px] tracking-[0.22em] uppercase text-espresso/70 group-hover:text-espresso font-sans">Menu</span>
            </button>
          </div>

          {/* Center: logo */}
          <Link to="/" className="flex flex-col items-center leading-none justify-self-center" data-testid="logo-link">
            {settings.logo_url ? (
              <img src={assetUrl(settings.logo_url)} alt={settings.brand_name} className="h-12 md:h-14 lg:h-16 w-auto object-contain" />
            ) : (
              <>
                <span className="font-serif text-2xl lg:text-3xl font-semibold tracking-tight text-espresso">{settings.brand_name}</span>
                <span className="font-accent text-[9px] lg:text-[10px] tracking-[0.35em] text-champagne uppercase whitespace-nowrap">{settings.brand_tagline}</span>
              </>
            )}
          </Link>

          {/* Right icons */}
          <div className="flex items-center gap-3 lg:gap-5 justify-self-end">
            <button onClick={() => setSearchOpen((s) => !s)} data-testid="search-toggle" aria-label="Search">
              <Search className="w-5 h-5 text-espresso hover:text-champagne transition-colors" />
            </button>
            <Link to={user ? "/account" : "/login"} data-testid="account-link" aria-label="Account" className="hidden sm:block">
              <User className="w-5 h-5 text-espresso hover:text-champagne transition-colors" />
            </Link>
            <Link to="/wishlist" className="relative" data-testid="wishlist-link" aria-label="Wishlist">
              <Heart className="w-5 h-5 text-espresso hover:text-champagne transition-colors" />
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-champagne text-espresso text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-semibold">{wishlist.length}</span>
              )}
            </Link>
            <button onClick={() => setDrawerOpen(true)} className="relative" data-testid="cart-drawer-toggle" aria-label="Shopping bag">
              <ShoppingBag className="w-5 h-5 text-espresso hover:text-champagne transition-colors" />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-espresso text-ivory text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-semibold" data-testid="cart-count">{count}</span>
              )}
            </button>
          </div>
        </div>

        {/* Search bar */}
        {searchOpen && (
          <form onSubmit={submitSearch} className="pb-4 pt-1 fade-in-up" data-testid="search-form">
            <div className="flex items-center border-b border-espresso/30 pb-2">
              <Search className="w-4 h-4 text-espresso/50 mr-3" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search abayas, hijabs, jilbabs..."
                className="w-full bg-transparent outline-none font-sans text-sm placeholder:text-espresso/40"
                data-testid="search-input"
              />
            </div>
          </form>
        )}
      </div>
    </header>

      {/* Category side slider */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-espresso/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[85%] max-w-md bg-ivory shadow-2xl p-6 overflow-y-auto fade-in-up">
            <div className="flex items-center justify-between mb-8">
              <div className="flex flex-col leading-none">
                {settings.logo_url ? (
                  <img src={assetUrl(settings.logo_url)} alt={settings.brand_name} className="h-12 w-auto object-contain" />
                ) : (
                  <>
                    <span className="font-serif text-2xl font-semibold text-espresso">{settings.brand_name}</span>
                    <span className="font-accent text-[9px] tracking-[0.35em] text-champagne uppercase">{settings.brand_tagline}</span>
                  </>
                )}
              </div>
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close"><X className="w-6 h-6" /></button>
            </div>

            <nav className="flex flex-col">
              <Link to="/" onClick={() => setMobileOpen(false)} className="py-3 border-b border-champagne/15 font-sans text-[15px] text-espresso" data-testid="mobile-nav-home">Home</Link>
              {categories.map((c) => (
                <Link key={c.key} to={`/category/${c.key}`} onClick={() => setMobileOpen(false)} className="py-3 border-b border-champagne/15 font-sans text-[15px] text-espresso" data-testid={`mobile-nav-cat-${c.key}`}>
                  {c.name}
                </Link>
              ))}
              {NAV_LINKS.filter((l) => ["New Arrivals", "Sale", "About Us", "Contact"].includes(l.name)).map((l) => (
                <Link key={l.path} to={l.path} onClick={() => setMobileOpen(false)} className={`py-3 border-b border-champagne/15 font-sans text-[15px] ${l.name === "Sale" ? "text-[#a13b2f] font-semibold" : "text-espresso"}`} data-testid={`mobile-nav-${l.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {l.name}
                </Link>
              ))}
            </nav>

            <p className="overline mt-8 mb-3">Shop by Category</p>
            <div className="grid grid-cols-2 gap-3">
              {categories.map((c) => (
                <Link key={c.key} to={`/category/${c.key}`} onClick={() => setMobileOpen(false)} className="relative rounded-xl overflow-hidden aspect-[4/3] group bg-beige" data-testid={`slider-category-${c.key}`}>
                  {c.image && <img src={assetUrl(c.image)} alt={c.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 to-transparent flex items-end p-3">
                    <span className="text-ivory text-sm font-serif leading-tight">{c.name}</span>
                  </div>
                </Link>
              ))}
            </div>

            <Link to={user ? "/account" : "/login"} onClick={() => setMobileOpen(false)} className="block py-4 mt-6 font-sans text-[15px] text-espresso border-t border-champagne/15">{user ? "My Account" : "Login / Register"}</Link>
          </div>
        </div>
      )}
    </>
  );
}
